import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, Plus, Upload, Download, BadgeCheck, Loader2, X, Archive, Send,
  AlertTriangle, FileText, FileSpreadsheet, Film, MessageSquare, Library,
  Wrench, Sparkles, Filter, Pencil, Eye, History, ClipboardList, BarChart3,
  BookOpen,
} from 'lucide-react';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ResourcePreviewDialog, ResourceVersionHistoryDialog, ResourceChangelogDialog,
} from '@/components/resources/ResourceDialogs';
import { ResourceAnalyticsDialog, TopFiveStrip } from '@/components/resources/ResourceAnalytics';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  documentation:        FileText,
  templates:            FileSpreadsheet,
  sample_questionnaires: Library,
  videos:               Film,
  faqs:                 MessageSquare,
  troubleshooting:      Wrench,
  ai_resources:         Sparkles,
};

const STATUS_TONE = {
  draft:      'border-muted-foreground/30 text-muted-foreground',
  published:  'border-success/30 text-success bg-success/5',
  deprecated: 'border-warning/30 text-warning bg-warning/5',
  archived:   'border-destructive/30 text-destructive bg-destructive/5',
};

export default function AdminResourceCenter({ embedded = false }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Resource Center Guides (P2) — deep-link support (?open=<seed_key>).
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  // Sprint A.1.3-γ — Analytics dialog + focused preview from Top-5 strip.
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [focusedPreview, setFocusedPreview] = useState(null);  // resource to preview from Top-5 click

  const caps = meta?.capabilities || {};

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived };
      if (q.trim()) params.q = q.trim();
      if (category !== 'all') params.category = category;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await adminApi.listResources(params);
      setItems(res.items || []);
    } catch (e) {
      toast.error(t('toasts.failedToLoad'), { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [includeArchived, q, category, statusFilter, t]);

  useEffect(() => {
    adminApi.resourcesMeta().then(setMeta).catch((e) =>
      toast.error(t('toasts.failedToLoad'), { description: formatApiError(e) })
    );
  }, [t]);

  useEffect(() => {
    const id = setTimeout(reload, 250);
    return () => clearTimeout(id);
  }, [reload]);

  // Resource Center Guides (P2) — ?open=<seed_key|id> deep-link from ModuleBanner.
  useEffect(() => {
    if (embedded || loading || !openParam || !items.length) return;
    const target = items.find((x) => x.seed_key === openParam || x.id === openParam);
    if (target) {
      setFocusedPreview(target);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [embedded, loading, openParam, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesForFilter = ['all', ...(meta?.categories || [])];

  return (
    <>
      {!embedded && (
        <Topbar
          title={t('resourceCenter.title', { defaultValue: 'Resource Center' })}
          subtitle={t('resourceCenter.subtitle', { defaultValue: 'Guides, templates, samples and documentation for Corporate Talent Hub.' })}
        />
      )}
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-resource-center">
        {!embedded && <ModuleBanner id="resourceCenter" icon={BookOpen} />}
        {/* Sprint A.1.3-γ — Top 5 most-used strip */}
        <TopFiveStrip
          onOpenAnalytics={() => setAnalyticsOpen(true)}
          onOpenResource={(it) => {
            const full = items.find((x) => x.id === it.id);
            setFocusedPreview(full || it);
          }}
        />
        <ResourceAnalyticsDialog open={analyticsOpen} onOpenChange={setAnalyticsOpen} />
        {focusedPreview && (
          <ResourcePreviewDialog
            open={!!focusedPreview}
            onOpenChange={(o) => { if (!o) setFocusedPreview(null); }}
            resource={focusedPreview}
            caps={caps}
            onChanged={reload}
          />
        )}
        {/* Search + filter bar */}
        <Card className="p-4 shadow-card" data-testid="resource-filters">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="resource-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('resourceCenter.searchPh', { defaultValue: 'Search title, description, tags, category, type…' })}
                className="pl-8 h-9"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[200px] h-9" data-testid="resource-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoriesForFilter.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c === 'all'
                      ? t('resourceCenter.allCategories', { defaultValue: 'All categories' })
                      : t(`resourceCenter.categories.${c}`, { defaultValue: c })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-9" data-testid="resource-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('resourceCenter.allStatuses', { defaultValue: 'All statuses' })}</SelectItem>
                {(meta?.statuses || []).map((s) => (
                  <SelectItem key={s} value={s}>{t(`resourceCenter.statuses.${s}`, { defaultValue: s })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={includeArchived ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setIncludeArchived((v) => !v)}
              className="h-9 gap-1.5"
              data-testid="resource-toggle-archived"
            >
              <Filter className="h-3.5 w-3.5" />
              {t('resourceCenter.showArchived', { defaultValue: 'Show archived' })}
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs" data-testid="resource-count-pill">
                {t('resourceCenter.resultCount', { count: items.length, defaultValue: '{{count}} results' })}
              </Badge>
              <Button
                variant="outline"
                className="h-9 gap-1.5"
                onClick={() => setAnalyticsOpen(true)}
                data-testid="resource-analytics-btn"
              >
                <BarChart3 className="h-4 w-4" /> {t('resourceCenter.analytics.cta', { defaultValue: 'Analytics' })}
              </Button>
              {caps.can_upload && (
                <Button
                  className="bg-gradient-primary h-9 gap-1.5"
                  onClick={() => setCreateOpen(true)}
                  data-testid="resource-new-btn"
                >
                  <Plus className="h-4 w-4" /> {t('resourceCenter.new', { defaultValue: 'New resource' })}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Grid */}
        {loading ? (
          <Card className="p-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-16 text-center space-y-3" data-testid="resource-empty">
            <Library className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="font-display text-lg font-bold tracking-tight">
              {t('resourceCenter.emptyTitle', { defaultValue: 'No resources yet' })}
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t('resourceCenter.emptySub', { defaultValue: 'Resources you create or upload here will be available to every HM Advisory consultant.' })}
            </p>
            {caps.can_upload && (
              <Button onClick={() => setCreateOpen(true)} className="bg-gradient-primary gap-1.5 mt-3" data-testid="resource-new-btn-empty">
                <Plus className="h-4 w-4" /> {t('resourceCenter.new', { defaultValue: 'New resource' })}
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="resource-grid">
            {items.map((r) => (
              <ResourceCard key={r.id} resource={r} caps={caps} onChanged={reload} />
            ))}
          </div>
        )}
      </div>

      <CreateResourceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        meta={meta}
        onCreated={reload}
      />
    </>
  );
}


function ResourceCard({ resource, caps, onChanged }) {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const isES = i18n.language?.startsWith('es');
  const Icon = CATEGORY_ICONS[resource.category] || FileText;
  const [busy, setBusy] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const isPreviewable = !!(resource.body_md || resource.questionnaire_payload);
  const isImportable = (resource.category === 'sample_questionnaires'
                       || resource.category === 'templates')
                       && !!resource.questionnaire_payload;

  const wrap = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      onChanged?.();
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setBusy(null);
    }
  };

  const download = () =>
    wrap('download', async () => {
      const res = await adminApi.downloadResource(resource.id);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.file_name || `${resource.title}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });

  const replaceFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(isES ? 'Archivo demasiado grande (>25 MB).' : 'File too large (>25 MB).');
      return;
    }
    await wrap('upload', () => adminApi.uploadResourceFile(resource.id, file),
      t('resourceCenter.uploadedToast', { defaultValue: 'File updated' }));
  };

  return (
    <Card className="p-5 shadow-card flex flex-col gap-3 hover:shadow-elegant transition-shadow"
          data-testid={`resource-card-${resource.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold tracking-tight truncate" title={tData(resource.title)}>
            {tData(resource.title)}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t(`resourceCenter.categories.${resource.category}`, { defaultValue: resource.category })}
            {' · '}
            {resource.resource_type || '—'}
          </p>
        </div>
        <Badge variant="outline" className={`text-xs uppercase tracking-wider ${STATUS_TONE[resource.status] || ''}`}
               data-testid={`resource-status-${resource.id}`}>
          {t(`resourceCenter.statuses.${resource.status}`, { defaultValue: resource.status })}
        </Badge>
      </div>

      {resource.description && (
        <p className="text-xs text-foreground/80 leading-relaxed line-clamp-3">{tData(resource.description)}</p>
      )}

      {(resource.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {resource.tags.slice(0, 6).map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>
      )}

      {/* Metadata strip */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.version', { defaultValue: 'Version' })}:</span> {resource.version}</span>
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.cthVersion', { defaultValue: 'CTH' })}:</span> {resource.compatible_cth_version || '—'}</span>
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.author', { defaultValue: 'Author' })}:</span> {resource.author || '—'}</span>
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.updated', { defaultValue: 'Updated' })}:</span> {resource.updated_at ? new Date(resource.updated_at).toLocaleDateString(getUiLocale()) : '—'}</span>
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.created', { defaultValue: 'Created' })}:</span> {resource.created_at ? new Date(resource.created_at).toLocaleDateString(getUiLocale()) : '—'}</span>
        <span><span className="font-medium text-foreground/70">{t('resourceCenter.fields.file', { defaultValue: 'File' })}:</span> {resource.file_name ? `${resource.file_name} (${(resource.file_size / 1024).toFixed(1)} KB)` : '—'}</span>
      </div>

      {/* Official badge */}
      {resource.official && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-primary"
             data-testid={`resource-official-${resource.id}`}>
          <BadgeCheck className="h-3.5 w-3.5" />
          {t('resourceCenter.officialBadge', { defaultValue: 'Official Corporate Talent Hub Resource' })}
        </div>
      )}

      {/* Analytics chips (views / downloads / imports) */}
      {resource.analytics && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`resource-analytics-${resource.id}`}>
          <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{resource.analytics.views || 0}</span>
          <span className="inline-flex items-center gap-1"><Download className="h-3 w-3" />{resource.analytics.downloads || 0}</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />{resource.analytics.imports || 0}</span>
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-1 mt-1 pt-2 border-t border-border/70">
        {isPreviewable && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-primary"
                  onClick={() => setPreviewOpen(true)}
                  data-testid={`resource-preview-${resource.id}`}>
            <Eye className="h-3 w-3" /> {t('resourceCenter.preview', { defaultValue: 'Preview' })}
          </Button>
        )}
        {caps.can_download && resource.file_id && (
          <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={download} disabled={busy === 'download'}
                  data-testid={`resource-download-${resource.id}`}>
            {busy === 'download' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {t('resourceCenter.download', { defaultValue: 'Download' })}
          </Button>
        )}
        {isImportable && caps.can_upload && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-primary" disabled={busy === 'import'}
                  onClick={() => wrap('import', async () => {
                    const res = await adminApi.importResourceToLibrary(resource.id);
                    toast.success(t('resourceCenter.importedToast', { defaultValue: 'Draft created in your Evaluation Library' }));
                    setTimeout(() => { window.location.href = `/admin/evaluation-library/${res.questionnaire_id}`; }, 250);
                  })}
                  data-testid={`resource-import-${resource.id}`}>
            {busy === 'import' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {t('resourceCenter.importToLibrary', { defaultValue: 'Import to My Library' })}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-muted-foreground"
                onClick={() => setVersionsOpen(true)}
                data-testid={`resource-versions-${resource.id}`}>
          <History className="h-3 w-3" /> {t('resourceCenter.versionHistory', { defaultValue: 'Version history' })}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-muted-foreground"
                onClick={() => setChangelogOpen(true)}
                data-testid={`resource-changelog-${resource.id}`}>
          <ClipboardList className="h-3 w-3" /> {t('resourceCenter.changelog', { defaultValue: 'Changelog' })}
        </Button>
        {caps.can_upload && resource.status !== 'archived' && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => replaceFile(e.target.files?.[0])}
              data-testid={`resource-upload-input-${resource.id}`}
            />
            <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => fileInputRef.current?.click()} disabled={busy === 'upload'}
                    data-testid={`resource-upload-${resource.id}`}>
              {busy === 'upload' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {resource.file_id
                ? t('resourceCenter.replaceFile', { defaultValue: 'Replace file' })
                : t('resourceCenter.uploadFile', { defaultValue: 'Upload file' })}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setEditOpen(true)}
                    data-testid={`resource-edit-${resource.id}`}>
              <Pencil className="h-3 w-3" /> {t('common.edit', { defaultValue: 'Edit' })}
            </Button>
          </>
        )}
        {caps.can_publish && resource.status === 'draft' && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-success" disabled={busy === 'publish'}
                  onClick={() => wrap('publish', () => adminApi.publishResource(resource.id), t('resourceCenter.publishedToast', { defaultValue: 'Resource published' }))}
                  data-testid={`resource-publish-${resource.id}`}>
            {busy === 'publish' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            {t('resourceCenter.publish', { defaultValue: 'Publish' })}
          </Button>
        )}
        {caps.can_publish && resource.status === 'published' && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-warning" disabled={busy === 'deprecate'}
                  onClick={() => wrap('deprecate', () => adminApi.deprecateResource(resource.id), t('resourceCenter.deprecatedToast', { defaultValue: 'Resource deprecated' }))}
                  data-testid={`resource-deprecate-${resource.id}`}>
            {busy === 'deprecate' ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
            {t('resourceCenter.deprecate', { defaultValue: 'Deprecate' })}
          </Button>
        )}
        {caps.can_archive && resource.status !== 'archived' && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-destructive ml-auto" disabled={busy === 'archive'}
                  onClick={() => wrap('archive', () => adminApi.archiveResource(resource.id), t('resourceCenter.archivedToast', { defaultValue: 'Resource archived' }))}
                  data-testid={`resource-archive-${resource.id}`}>
            {busy === 'archive' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
            {t('resourceCenter.archive', { defaultValue: 'Archive' })}
          </Button>
        )}
      </div>

      <EditResourceDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        resource={resource}
        meta={null}
        onSaved={onChanged}
      />
      <ResourcePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        resource={resource}
        caps={caps}
        onChanged={onChanged}
      />
      <ResourceVersionHistoryDialog
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        resource={resource}
      />
      <ResourceChangelogDialog
        open={changelogOpen}
        onOpenChange={setChangelogOpen}
        resource={resource}
      />
    </Card>
  );
}


function CreateResourceDialog({ open, onOpenChange, meta, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: '', description: '', resource_type: 'guide', category: 'documentation',
    tags: '', version: '1.0', author: '', compatible_cth_version: '1.2', official: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      title: '', description: '', resource_type: 'guide', category: 'documentation',
      tags: '', version: '1.0', author: '', compatible_cth_version: '1.2', official: true,
    });
  }, [open]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error(t('resourceCenter.create.titleRequired', { defaultValue: 'Title is required' }));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      await adminApi.createResource(payload);
      toast.success(t('resourceCenter.create.successToast', { defaultValue: 'Resource created (draft)' }));
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="resource-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('resourceCenter.create.title', { defaultValue: 'New resource' })}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('resourceCenter.fields.title', { defaultValue: 'Title' })} required className="sm:col-span-2">
            <Input data-testid="resource-create-title" value={form.title} onChange={(e) => setField('title', e.target.value)} />
          </Field>
          <Field label={t('resourceCenter.fields.description', { defaultValue: 'Description' })} className="sm:col-span-2">
            <Textarea data-testid="resource-create-description" rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} />
          </Field>
          <Field label={t('resourceCenter.fields.category', { defaultValue: 'Category' })}>
            <Select value={form.category} onValueChange={(v) => setField('category', v)}>
              <SelectTrigger data-testid="resource-create-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.categories || []).map((c) => (
                  <SelectItem key={c} value={c}>{t(`resourceCenter.categories.${c}`, { defaultValue: c })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('resourceCenter.fields.resourceType', { defaultValue: 'Resource type' })}>
            <Input data-testid="resource-create-type" value={form.resource_type} onChange={(e) => setField('resource_type', e.target.value)}
                   placeholder="guide, template, sample, video, faq…" />
          </Field>
          <Field label={t('resourceCenter.fields.tags', { defaultValue: 'Tags' })} className="sm:col-span-2">
            <Input data-testid="resource-create-tags" value={form.tags} onChange={(e) => setField('tags', e.target.value)}
                   placeholder="comma, separated, list" />
          </Field>
          <Field label={t('resourceCenter.fields.version', { defaultValue: 'Version' })}>
            <Input data-testid="resource-create-version" value={form.version} onChange={(e) => setField('version', e.target.value)} />
          </Field>
          <Field label={t('resourceCenter.fields.cthVersion', { defaultValue: 'Compatible CTH version' })}>
            <Input data-testid="resource-create-cth" value={form.compatible_cth_version} onChange={(e) => setField('compatible_cth_version', e.target.value)} />
          </Field>
          <Field label={t('resourceCenter.fields.author', { defaultValue: 'Author' })} className="sm:col-span-2">
            <Input data-testid="resource-create-author" value={form.author} onChange={(e) => setField('author', e.target.value)} placeholder="leave blank to use your own name" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}><X className="h-4 w-4" /></Button>
          <Button onClick={submit} disabled={busy} className="bg-gradient-primary gap-1.5" data-testid="resource-create-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('resourceCenter.create.cta', { defaultValue: 'Create draft' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditResourceDialog({ open, onOpenChange, resource, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && resource) setForm({
      title: resource.title || '',
      description: resource.description || '',
      resource_type: resource.resource_type || '',
      tags: (resource.tags || []).join(', '),
      version: resource.version || '',
      author: resource.author || '',
      compatible_cth_version: resource.compatible_cth_version || '',
      official: !!resource.official,
    });
  }, [open, resource]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean) };
      await adminApi.updateResource(resource.id, payload);
      toast.success(t('toasts.saveSuccess', { defaultValue: 'Saved' }));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!resource) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`resource-edit-dialog-${resource.id}`}>
        <DialogHeader>
          <DialogTitle>{t('resourceCenter.edit.title', { defaultValue: 'Edit resource' })}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('resourceCenter.fields.title', { defaultValue: 'Title' })} className="sm:col-span-2">
            <Input value={form.title || ''} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          </Field>
          <Field label={t('resourceCenter.fields.description', { defaultValue: 'Description' })} className="sm:col-span-2">
            <Textarea rows={3} value={form.description || ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </Field>
          <Field label={t('resourceCenter.fields.tags', { defaultValue: 'Tags' })} className="sm:col-span-2">
            <Input value={form.tags || ''} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))} />
          </Field>
          <Field label={t('resourceCenter.fields.version', { defaultValue: 'Version' })}>
            <Input value={form.version || ''} onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} />
          </Field>
          <Field label={t('resourceCenter.fields.cthVersion', { defaultValue: 'Compatible CTH version' })}>
            <Input value={form.compatible_cth_version || ''} onChange={(e) => setForm((p) => ({ ...p, compatible_cth_version: e.target.value }))} />
          </Field>
          <Field label={t('resourceCenter.fields.author', { defaultValue: 'Author' })} className="sm:col-span-2">
            <Input value={form.author || ''} onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={busy} className="bg-gradient-primary">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, required, className, children }) {
  return (
    <div className={`space-y-1 ${className || ''}`}>
      <Label className="text-xs">{label}{required && <span className="text-destructive ml-1">*</span>}</Label>
      {children}
    </div>
  );
}
